import apiClient from "../api-client.js";

/**
 * bundle.social publishing endpoints.
 *
 * Content lives in Markezy; bundle.social only publishes. A piece is scheduled
 * as a whole (one post with every connected network, blog excluded) at the
 * piece's `scheduled_at`. Only internal users can call these.
 */

const BASE = "/content-generation";

/** Error text the backend returns (`{error}` for publishing errors, DRF shapes otherwise). */
export const publishingErrorText = (err, fallback) => {
  const data = err?.response?.data;
  if (typeof data?.error === "string") return data.error;
  if (typeof data?.detail === "string") return data.detail;
  if (data && typeof data === "object") {
    const first = Object.values(data).flat().find((value) => typeof value === "string");
    if (first) return first;
  }
  return fallback;
};

// ─── Client connections ──────────────────────────────────────────────────────

/** Team, connected accounts and sync state of a client. */
export const getClientSocial = async (clientId) => {
  const response = await apiClient.get(`${BASE}/social/clients/${clientId}/`);
  return response.data;
};

/**
 * URL of the hosted bundle.social portal, always with every publishable network.
 * `returnPath` (an app path) is where the portal sends the user back; the client's networks tab by default.
 */
export const createConnectLink = async (clientId, returnPath) => {
  const payload = {};
  if (returnPath) payload.return_path = returnPath;
  const response = await apiClient.post(`${BASE}/social/clients/${clientId}/connect-link/`, payload);
  return response.data.url;
};

/** Check the connections against bundle.social and refresh the local accounts. */
export const syncClientSocial = async (clientId) => {
  const response = await apiClient.post(`${BASE}/social/clients/${clientId}/sync/`, null, {
    timeout: 30000,
  });
  return response.data;
};

export const disconnectSocialAccount = async (clientId, platform) => {
  const response = await apiClient.post(`${BASE}/social/clients/${clientId}/disconnect/`, {
    platform,
  });
  return response.data;
};

// ─── Piece scheduling ────────────────────────────────────────────────────────

/** Schedule the piece in every connected network. Returns the SocialPost of each network. */
export const schedulePiece = async (pieceId, scheduledAt) => {
  const response = await apiClient.post(
    `${BASE}/pieces/${pieceId}/schedule/`,
    scheduledAt ? { scheduled_at: scheduledAt } : {},
    { timeout: 60000 }
  );
  return response.data;
};

/** Cancel the scheduled post. The piece keeps its date. */
export const cancelPieceSchedule = async (pieceId) => {
  const response = await apiClient.post(`${BASE}/pieces/${pieceId}/cancel-schedule/`, null, {
    timeout: 30000,
  });
  return response.data;
};

/** Networks in error are scheduled again, together, at `scheduledAt` (ISO, future). */
export const retryPiecePublication = async (pieceId, scheduledAt) => {
  const response = await apiClient.post(`${BASE}/pieces/${pieceId}/retry-publication/`, { scheduled_at: scheduledAt }, {
    timeout: 30000,
  });
  return response.data;
};

/**
 * Move the piece's date. If the piece is scheduled the backend also moves the
 * bundle.social post (409 when it is already being published).
 */
export const updatePieceScheduledAt = async (pieceId, scheduledAt) => {
  const response = await apiClient.patch(
    `${BASE}/pieces/${pieceId}/`,
    { scheduled_at: scheduledAt || null },
    { timeout: 30000 }
  );
  return response.data;
};

// ─── Video and format of an adaptation ───────────────────────────────────────

// Django forwards the file to bundle.social within the same request (up to ~100 s after the bytes arrive).
const VIDEO_UPLOAD_TIMEOUT_MS = 10 * 60 * 1000;

/**
 * Upload a video (.mp4/.mov, ≤ 150 MB) to an adaptation. Returns the new media item
 * (same shape as the items of `adaptation.images`, with `media_type: "video"`).
 * `onProgress(fraction)` follows the bytes sent; `signal` cancels it.
 */
export const uploadAdaptationVideo = async (adaptationId, file, { onProgress, signal } = {}) => {
  const formData = new FormData();
  formData.append("video", file);
  const response = await apiClient.post(`${BASE}/adaptations/${adaptationId}/add_video/`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: VIDEO_UPLOAD_TIMEOUT_MS,
    signal,
    onUploadProgress: (event) => {
      const total = event.total || file.size;
      if (onProgress && total) onProgress(Math.min(1, event.loaded / total));
    },
  });
  return Array.isArray(response.data) ? response.data[0] : response.data;
};

/** Error text of a failed upload (size limit of the server, lost connection, backend message). */
const uploadErrorText = (err, tooBig, fallback) => {
  if (err?.response?.status === 413) return tooBig;
  if (!err?.response) {
    return err?.code === "ECONNABORTED"
      ? "La subida ha tardado demasiado. Vuelve a intentarlo."
      : "Se ha perdido la conexión durante la subida. Vuelve a intentarlo.";
  }
  return publishingErrorText(err, fallback);
};

export const videoUploadErrorText = (err) =>
  uploadErrorText(err, "El vídeo supera el tamaño máximo que admite el servidor.", "No se ha podido subir el vídeo.");

// ─── LinkedIn document (PDF) ──────────────────────────────────────────────────

/**
 * Attach a PDF (≤ 50 MB) to a LinkedIn adaptation with the title shown on the post.
 * Returns the new media item (`media_type: "document"`, `document` = file URL).
 */
export const uploadAdaptationDocument = async (adaptationId, file, title) => {
  const formData = new FormData();
  formData.append("document", file);
  formData.append("title", title);
  const response = await apiClient.post(`${BASE}/adaptations/${adaptationId}/add_document/`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: VIDEO_UPLOAD_TIMEOUT_MS,
  });
  return response.data;
};

export const documentUploadErrorText = (err) =>
  uploadErrorText(err, "El documento supera el tamaño máximo que admite el servidor.", "No se ha podido adjuntar el documento.");

const patchAdaptation = async (adaptationId, fields) => {
  const response = await apiClient.patch(`${BASE}/adaptations/${adaptationId}/`, fields, { timeout: 30000 });
  return response.data;
};

/** Title of a LinkedIn document, shown on the post. Returns the media item. */
export const updateDocumentTitle = async (documentId, title) => {
  const response = await apiClient.patch(`${BASE}/adaptation-images/${documentId}/`, { title }, { timeout: 30000 });
  return response.data;
};

/** Crops an image to `{ x, y, width, height }` (pixels of the image as shown). Returns the media item. */
export const cropAdaptationImage = async (imageId, box) => {
  const response = await apiClient.post(`${BASE}/adaptation-images/${imageId}/crop/`, box, { timeout: 60000 });
  return response.data;
};

/** Publication format of an adaptation: `post` | `reel` | `story` | `video` (depends on the network). */
export const updateAdaptationFormat = (adaptationId, format) => patchAdaptation(adaptationId, { format });

/**
 * Google Business Profile post button: `{ type, url }`. `type` is BOOK | ORDER | SHOP | LEARN_MORE |
 * SIGN_UP (all need `url`) | CALL ("Llamar ahora") | "" (none).
 */
export const updateAdaptationCallToAction = (adaptationId, { type, url }) =>
  patchAdaptation(adaptationId, { call_to_action: type, call_to_action_url: url });

export const isUploadCancelled = (err) => err?.code === "ERR_CANCELED" || err?.name === "CanceledError";
